import { Controller, useWatch, type Control } from 'react-hook-form';
import {
  DateField,
  SegmentedControl,
  SelectField,
  TextField,
  ToggleField,
  type SegmentedOption,
  type SelectOption,
} from '../../../shared/ui/index.js';
import type { PaymentFormValues } from '../model/payment-form-values.js';

const KIND_OPTIONS: SegmentedOption[] = [
  { value: 'auto_debit', label: 'Автосписання' },
  { value: 'manual', label: 'Вручну' },
];

const FREQUENCY_OPTIONS: SelectOption[] = [
  { value: 'monthly', label: 'Щомісяця' },
  { value: 'quarterly', label: 'Щокварталу' },
  { value: 'half_year', label: 'Раз на пів року' },
  { value: 'yearly', label: 'Щороку' },
  { value: 'custom', label: 'Інше' },
];

const CUSTOM_UNIT_OPTIONS: SegmentedOption[] = [
  { value: 'month', label: 'Місяці' },
  { value: 'year', label: 'Роки' },
];

const COMMON_CURRENCIES = ['UAH', 'USD', 'EUR', 'PLN', 'GBP'];

const currencyOptionsOf = (selected: string): SelectOption[] => {
  const codes = [...new Set([selected, ...COMMON_CURRENCIES])].filter((code) => code !== '');

  return codes.map((code) => ({ value: code, label: code }));
};

type ScheduleStepProps = {
  control: Control<PaymentFormValues>;
};

export const ScheduleStep = ({ control }: ScheduleStepProps) => {
  const isVariableAmount = useWatch({ control, name: 'isVariableAmount' });
  const frequency = useWatch({ control, name: 'frequency' });
  const currency = useWatch({ control, name: 'currency' });
  const isCustomFrequency = frequency === 'custom';

  return (
    <>
      <Controller
        control={control}
        name="name"
        render={({ field, fieldState }) => (
          <TextField
            label="Назва"
            name={field.name}
            value={field.value}
            onChange={field.onChange}
            onBlur={field.onBlur}
            ref={field.ref}
            placeholder="Netflix"
            error={fieldState.error?.message}
          />
        )}
      />

      {!isVariableAmount && (
        <Controller
          control={control}
          name="amount"
          render={({ field, fieldState }) => (
            <TextField
              label="Сума"
              name={field.name}
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              ref={field.ref}
              inputMode="decimal"
              placeholder="299.00"
              error={fieldState.error?.message}
            />
          )}
        />
      )}

      <Controller
        control={control}
        name="currency"
        render={({ field, fieldState }) => (
          <SelectField
            label="Валюта"
            name={field.name}
            options={currencyOptionsOf(currency)}
            value={field.value}
            onChange={field.onChange}
            onBlur={field.onBlur}
            ref={field.ref}
            error={fieldState.error?.message}
          />
        )}
      />

      <Controller
        control={control}
        name="isVariableAmount"
        render={({ field }) => (
          <ToggleField
            label="Сума щоразу інша"
            name={field.name}
            checked={field.value}
            onChange={field.onChange}
            onBlur={field.onBlur}
            ref={field.ref}
            description="Суми наперед немає — введете її, коли прийде рахунок"
          />
        )}
      />

      <Controller
        control={control}
        name="kind"
        render={({ field }) => (
          <SegmentedControl
            label="Спосіб оплати"
            name={field.name}
            options={KIND_OPTIONS}
            value={field.value}
            onChange={(value) => {
              field.onChange(value);
            }}
            onBlur={field.onBlur}
          />
        )}
      />

      <Controller
        control={control}
        name="frequency"
        render={({ field }) => (
          <SelectField
            label="Періодичність"
            name={field.name}
            options={FREQUENCY_OPTIONS}
            value={field.value}
            onChange={field.onChange}
            onBlur={field.onBlur}
            ref={field.ref}
          />
        )}
      />

      {isCustomFrequency && (
        <Controller
          control={control}
          name="customIntervalUnit"
          render={({ field }) => (
            <SegmentedControl
              label="Одиниця періоду"
              name={field.name}
              options={CUSTOM_UNIT_OPTIONS}
              value={field.value}
              onChange={(value) => {
                field.onChange(value);
              }}
              onBlur={field.onBlur}
            />
          )}
        />
      )}

      {isCustomFrequency && (
        <Controller
          control={control}
          name="customIntervalCount"
          render={({ field, fieldState }) => (
            <TextField
              label="Раз на скільки"
              name={field.name}
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              ref={field.ref}
              inputMode="numeric"
              hint="Наприклад 2 — раз на два місяці"
              error={fieldState.error?.message}
            />
          )}
        />
      )}

      <Controller
        control={control}
        name="startDate"
        render={({ field, fieldState }) => (
          <DateField
            label="Дата списання"
            name={field.name}
            value={field.value}
            onChange={field.onChange}
            onBlur={field.onBlur}
            ref={field.ref}
            hint="Перше списання, далі за розкладом від цього числа. У коротких місяцях візьмемо останній день"
            error={fieldState.error?.message}
          />
        )}
      />
    </>
  );
};
